// Resolves private S3 blob keys → presigned GET URLs via POST /v1/assets/batch.
// Coalesces concurrent requests (many <img>s) into batched calls of ≤100 keys
// and caches URLs with a refresh margin under the 1h server TTL.

import { presignGetAssets } from "./apiClient";

const TTL_MS = 55 * 60 * 1000; // refresh before the server's 1h expiry
const BATCH_MAX = 100;

interface CacheEntry {
  url: string | null;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();

let queue = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushWaiters: { resolve: () => void }[] = [];

function scheduleFlush(): Promise<void> {
  return new Promise((resolve) => {
    flushWaiters.push({ resolve });
    if (flushTimer == null) {
      flushTimer = setTimeout(flush, 8);
    }
  });
}

async function flush(): Promise<void> {
  flushTimer = null;
  const keys = [...queue];
  queue = new Set();
  const waiters = flushWaiters;
  flushWaiters = [];

  for (let i = 0; i < keys.length; i += BATCH_MAX) {
    const chunk = keys.slice(i, i + BATCH_MAX);
    try {
      const assets = await presignGetAssets(chunk);
      const now = Date.now();
      for (const a of assets) {
        cache.set(a.key, { url: a.status === "present" ? a.url ?? null : null, expiresAt: now + TTL_MS });
      }
      // Keys the server didn't return at all → mark null so we don't loop forever.
      for (const k of chunk) {
        if (!cache.has(k)) cache.set(k, { url: null, expiresAt: now + TTL_MS });
      }
    } catch {
      const now = Date.now();
      for (const k of chunk) cache.set(k, { url: null, expiresAt: now + 30_000 });
    }
  }
  for (const w of waiters) w.resolve();
}

/** Resolve a single s3Key to a presigned GET URL (or null if missing). */
export async function resolveAsset(s3Key: string): Promise<string | null> {
  const hit = cache.get(s3Key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;

  const existing = inflight.get(s3Key);
  if (existing) return existing;

  const p = (async () => {
    queue.add(s3Key);
    await scheduleFlush();
    inflight.delete(s3Key);
    return cache.get(s3Key)?.url ?? null;
  })();
  inflight.set(s3Key, p);
  return p;
}

/** Synchronous cache peek (no fetch). */
export function peekAsset(s3Key: string): string | null | undefined {
  const hit = cache.get(s3Key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;
  return undefined;
}

export function clearAssetCache(): void {
  cache.clear();
  inflight.clear();
  queue = new Set();
}
